import React from 'react';

const TEN_BLOCK_COLOR = '#FF6B6B';
const ONE_BLOCK_COLOR = '#4ECDC4';

function getSizes(w, h) {
  const portrait = h > w;

  // Desktop / large tablet — fixed comfortable size
  if (w > 768) return { cs: 18, gap: 4, pad: 5, bgap: 10, cols: 2 };

  // For phones/tablets, derive circle size from available height so blocks
  // always fit without overflowing.
  //
  // Overhead = everything on screen except equation-row content:
  //   title + scorebar + nav (maybe) + card padding + instruction +
  //   legend + buttons + gaps + app padding ≈ 380px (conservative)
  //
  // Worst case rows:
  //   portrait 1-col: 4 ten-blocks + 1 ones row = 5 rows
  //   landscape 2-col: ceil(4/2) + 1 = 3 rows
  //
  // Row height formula: 2*cs + gap(0.22cs) + 2*pad(0.28cs) ≈ 2.78*cs
  // Plus bgaps between rows (4 * 0.5cs) + number-label (1.6cs)
  // Total height ≈ cs * (5*2.78 + 4*0.5 + 1.6) = cs * 17.5  (portrait)
  //              ≈ cs * (3*2.78 + 2*0.5 + 1.6) = cs * 11.1  (landscape)

  let cs, cols;
  if (portrait) {
    const available = Math.max(60, h - 380);
    cs = Math.max(8, Math.min(18, Math.floor(available / 17.5)));
    cols = 1;
  } else {
    const available = Math.max(60, h - 300);
    cs = Math.max(8, Math.min(14, Math.floor(available / 11.1)));
    cols = 2;
  }

  const gap  = Math.max(2, Math.round(cs * 0.22));
  const pad  = Math.max(2, Math.round(cs * 0.28));
  const bgap = Math.max(4, Math.round(cs * 0.5));
  return { cs, gap, pad, bgap, cols };
}

function useSizes() {
  const [sizes, setSizes] = React.useState(() => getSizes(window.innerWidth, window.innerHeight));
  React.useEffect(() => {
    const onResize = () => setSizes(getSizes(window.innerWidth, window.innerHeight));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return sizes;
}

function TenBlock({ color, s }) {
  const circles = Array.from({ length: 10 });
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        gap: s.gap,
        backgroundColor: 'rgba(255,255,255,0.25)',
        borderRadius: 6,
        padding: s.pad,
        border: `2px solid rgba(255,255,255,0.5)`,
      }}
    >
      {circles.map((_, i) => (
        <div
          key={i}
          style={{
            width: s.cs,
            height: s.cs,
            borderRadius: '50%',
            backgroundColor: color,
            border: '2px solid rgba(255,255,255,0.8)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      ))}
    </div>
  );
}

function OnesRow({ count, color, s }) {
  if (count === 0) return null;
  const circles = Array.from({ length: count });
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(5, 1fr)`,
        gridTemplateRows: 'repeat(2, 1fr)',
        gap: s.gap,
        backgroundColor: 'rgba(255,255,255,0.15)',
        borderRadius: 6,
        padding: s.pad,
        border: `2px dashed rgba(255,255,255,0.4)`,
        alignContent: 'start',
        minWidth: s.cs * 5 + s.gap * 4 + s.pad * 2 + 4,
      }}
    >
      {circles.map((_, i) => (
        <div
          key={i}
          style={{
            width: s.cs,
            height: s.cs,
            borderRadius: '50%',
            backgroundColor: color,
            border: '2px solid rgba(255,255,255,0.8)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      ))}
    </div>
  );
}

export default function NumberBlocks({ value, tenColor, oneColor }) {
  const tens = Math.floor(value / 10);
  const ones = value % 10;
  const s = useSizes();

  const tColor = tenColor || TEN_BLOCK_COLOR;
  const oColor = oneColor || ONE_BLOCK_COLOR;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${s.cols}, auto)`,
          gap: s.bgap,
          alignItems: 'start',
          justifyContent: 'start',
        }}
      >
        {Array.from({ length: tens }).map((_, i) => (
          <TenBlock key={i} color={tColor} s={s} />
        ))}
        {ones > 0 && <OnesRow count={ones} color={oColor} s={s} />}
        {value === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 18 }}>0</div>
        )}
      </div>
      <div
        style={{
          fontSize: s.cs * 1.6,
          fontWeight: 900,
          color: '#fff',
          textShadow: '0 2px 8px rgba(0,0,0,0.3)',
          letterSpacing: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}
